import axios from 'axios';

import { BFF_BASE_URL } from '../auth/authApi';

export type UploadStatus = 'stored' | 'queued' | 'processing' | 'ready' | 'failed';

export const UPLOAD_STATUSES: UploadStatus[] = ['stored', 'queued', 'processing', 'ready', 'failed'];

/** The kinds the type filter offers (backend uploads_admin.KIND_PATTERNS). */
export const UPLOAD_KINDS = [
    { value: 'image', label: 'Images' },
    { value: 'pdf', label: 'PDF' },
    { value: 'epub', label: 'EPUB' },
    { value: 'docx', label: 'Word' },
    { value: 'xlsx', label: 'Excel' },
    { value: 'text', label: 'Text, CSV, Markdown' },
];

export const STATUS_LABELS: Record<UploadStatus, string> = {
    stored: 'Stored',
    queued: 'Queued',
    processing: 'Processing',
    ready: 'Ready',
    failed: 'Failed',
};

/** One row of the list: enough to scan many uploads and see which carry EXIF. */
export interface AdminUpload {
    id: string;
    user_id: string;
    user_name: string;
    name: string;
    name_source: 'filename' | 'llm' | 'user';
    original_filename: string;
    mime_type: string;
    size_bytes: number;
    status: UploadStatus;
    status_detail: string | null;
    source_message_id: string | null;
    created_at: string;
    updated_at: string;
    has_metadata: boolean;
    has_gps: boolean;
    taken_at: string | null;
    camera: string | null;
}

export interface UploadPage {
    items: AdminUpload[];
    next_cursor: string | null;
}

export interface UploadFilters {
    user_id?: string;
    status?: string;
    type?: string;
    q?: string;
}

export interface Tally {
    count: number;
    bytes: number;
}

export interface UploadsSummary {
    total: number;
    total_bytes: number;
    by_status: Record<UploadStatus, Tally>;
    by_type: Array<{ mime_type: string; count: number; bytes: number }>;
    by_user: Array<{ user_id: string; user_name: string; count: number; bytes: number }>;
    with_metadata: number;
    with_gps: number;
}

export interface ExifGps {
    latitude?: number;
    longitude?: number;
    altitude_m?: number;
    direction_deg?: number;
    speed_kmh?: number;
    timestamp?: string;
    tags?: Record<string, unknown>;
}

/** What a picture said about itself (backend image_metadata.read_metadata). */
export interface ImageMetadata {
    image: { format?: string; width?: number; height?: number; mode?: string; orientation?: string };
    taken_at: string | null;
    camera: Record<string, unknown>;
    settings: Record<string, unknown>;
    gps: ExifGps | null;
    tags: Record<string, unknown>;
}

/** What the ingestion worker extracted: an image's caption and transcribed text, and its tags. */
export interface ExtractedKnowledge {
    source_type: string;
    content: string;
    tags: string[];
    /** What the user has said about the file since; it outranks `content`. */
    notes?: string[];
    ingested_at: string;
}

/** A face found in a picture, and who it is taken to be (backend people.faces_view). */
export interface DetectedFace {
    id: string;
    person_id: string | null;
    person_name: string | null;
    is_user: boolean;
    /** `pet` for a cat or a dog (then `species` says which), `person` for a face. */
    kind: 'person' | 'pet';
    species: 'dog' | 'cat' | null;
    /**
     * `confirmed`: the user said so, on a face. `suggested`: matched to a confirmed face.
     * `manual`: the user said so, with no face to point to (upload_people). `null`: nobody.
     */
    assignment: 'confirmed' | 'suggested' | 'manual' | null;
    /** Cosine similarity behind a suggestion (0 to 1). */
    match_score: number | null;
    /** How sure the detector is that this is a face (0 to 1). `null` for a `manual` tag: no face was found. */
    det_score: number | null;
    /** `null` for a `manual` tag. */
    model: string | null;
    /** Fractions of the picture's width and height. `null` for a `manual` tag: no face was found. */
    bbox: { x: number; y: number; w: number; h: number } | null;
    /** A small data URL of the face, `null` when the file is gone or (a `manual` tag) there is none. */
    avatar: string | null;
}

export interface UploadDetail extends AdminUpload {
    sha256: string;
    /** Every face found in the picture (images only; empty for anything else). */
    faces?: DetectedFace[];
    storage: {
        given_filename: string;
        directory: string;
        file_present: boolean;
        size_on_disk: number | null;
        size_matches: boolean | null;
    };
    metadata: ImageMetadata | null;
    knowledge: ExtractedKnowledge | null;
    source_message: {
        id: string;
        role: string;
        channel: string | null;
        status: string | null;
        timestamp: string;
        content: string;
    } | null;
    same_file_elsewhere: Array<{ id: string; user_id: string; user_name: string; name: string }>;
}

const uploadsUrl = (path = '') => `${BFF_BASE_URL}/admin/uploads${path}`;

const idPath = (id: string) => `/${encodeURIComponent(id)}`;

export const listUploads = async (
    filters: UploadFilters,
    cursor?: string,
    limit = 50
): Promise<UploadPage> => {
    const response = await axios.get<UploadPage>(uploadsUrl(), {
        params: { ...filters, cursor, limit },
    });
    return response.data;
};

export const getUploadsSummary = async (userId?: string): Promise<UploadsSummary> => {
    const response = await axios.get<UploadsSummary>(uploadsUrl('/summary'), {
        params: { user_id: userId || undefined },
    });
    return response.data;
};

export const getUpload = async (id: string): Promise<UploadDetail> => {
    const response = await axios.get<UploadDetail>(uploadsUrl(idPath(id)));
    return response.data;
};

export const renameUpload = async (id: string, name: string): Promise<{ id: string; name: string }> => {
    const response = await axios.patch(uploadsUrl(idPath(id)), { name });
    return response.data;
};

/** Puts a failed upload back on the ingestion queue. 409 (with a reason) when it is not failed or cannot be read. */
export const retryUpload = async (id: string): Promise<{ id: string; status: UploadStatus }> => {
    const response = await axios.post(uploadsUrl(`${idPath(id)}/retry`));
    return response.data;
};

export const refreshMetadata = async (id: string): Promise<{ id: string; metadata: ImageMetadata }> => {
    const response = await axios.post(uploadsUrl(`${idPath(id)}/metadata/refresh`));
    return response.data;
};

export const deleteUpload = async (
    id: string
): Promise<{ id: string; deleted: boolean; file_removed: boolean | null }> => {
    const response = await axios.delete(uploadsUrl(idPath(id)));
    return response.data;
};

/** The stored file, as a blob: an `<img src>` cannot carry the admin's login. */
export const fetchUploadFile = async (id: string): Promise<Blob> => {
    const response = await axios.get<Blob>(uploadsUrl(`${idPath(id)}/content`), { responseType: 'blob' });
    return response.data;
};

/** What went wrong, in words, from a failed request. */
export const errorMessage = (error: unknown, fallback: string): string =>
    axios.isAxiosError(error) ? error.response?.data?.error || error.message : fallback;

// ---------------------------------------------------------------- display

export const isImage = (mimeType: string): boolean => mimeType.startsWith('image/');

const KIND_LABELS: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/epub+zip': 'EPUB',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'text/csv': 'CSV',
    'text/plain': 'Text',
    'text/markdown': 'Markdown',
    'image/png': 'PNG',
    'image/jpeg': 'JPEG',
    'image/webp': 'WebP',
};

export const kindLabel = (mimeType: string): string => KIND_LABELS[mimeType] ?? mimeType;

/** 1536 -> "1.5 KB". */
export const formatBytes = (bytes: number): string => {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = bytes / 1024;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
    }
    return `${value >= 10 ? Math.round(value) : Math.round(value * 10) / 10} ${units[unit]}`;
};

/** 0.008333 -> "1/120 s"; 2 -> "2 s". */
export const formatShutter = (seconds: number): string =>
    seconds > 0 && seconds < 1 ? `1/${Math.round(1 / seconds)} s` : `${Math.round(seconds * 100) / 100} s`;

/** A location as a link to OpenStreetMap. Opening it sends the coordinates there, so it is a click, never a load. */
export const mapLink = (latitude: number, longitude: number): string =>
    `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=16/${latitude}/${longitude}`;

/** A tag value for a table cell: text stays text, everything else is compact JSON. */
export const formatTagValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }
    return typeof value === 'string' ? value : JSON.stringify(value);
};

/** A camera's own date ("2026-09-20T14:03:11-03:00") for a person, without pretending it is in their time zone. */
export const formatTakenAt = (takenAt: string): string => {
    const match = takenAt.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (!match) {
        return takenAt;
    }
    const [, year, month, day, hours, minutes, seconds] = match;
    const offset = takenAt.match(/([+-]\d{2}:\d{2})$/)?.[1];
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}${offset ? ` (UTC${offset})` : ''}`;
};
